import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  BankUpdateItem,
  FormErrorSummary,
  SelectField,
  TextareaField,
  applyServerErrors,
  zRequiredString,
} from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';

import { useResolveBankUpdate } from '../api/mutations';
import { dealDocumentsQuery } from '../api/queries';
import { requirementsOfBankUpdate } from '../model/documents';
import { LinkedRequirements } from './LinkedRequirements';

const schema = z.object({
  outcome: z.enum(['resolved', 'waived', 'approved', 'declined']),
  resolution: zRequiredString(),
});
type Values = z.infer<typeof schema>;

/** Closes an open bank update. Replying to a query also returns the deal to bank review. */
export function ResolveBankUpdateDialog({
  deal,
  update,
  onClose,
}: {
  deal: Schema<'Deal'>;
  update: Schema<'BankUpdate'>;
  onClose: () => void;
}) {
  const { t } = useTranslation('deals');
  const resolve = useResolveBankUpdate(deal.id);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const isQuery = update.kind === 'query' || update.kind === 'document_request';
  const outcomes: Values['outcome'][] =
    update.kind === 'deviation'
      ? ['approved', 'declined']
      : isQuery
        ? ['resolved']
        : ['resolved', 'waived'];
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { outcome: outcomes[0], resolution: '' },
  });
  // The documents the bank asked for in this update: uploaded right here, without leaving the reply.
  const documents = useQuery({ ...dealDocumentsQuery(deal.id), enabled: isQuery });
  const requested = requirementsOfBankUpdate(documents.data ?? [], update.id);
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await resolve.mutateAsync({
        updateId: update.id,
        body: { outcome: v.outcome, resolution: v.resolution.trim() },
      });
      toast.done(isQuery ? t('actions.respond_query.done') : t('bank.closed'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['resolution']);
        setProblem(e);
      }
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="form"
        title={
          isQuery
            ? t('actions.respond_query.title', { bank: deal.bank.code })
            : t('bank.closeTitle')
        }
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={resolve.isPending} onClick={() => void submit()}>
              {isQuery ? t('bank.reply') : t('bank.close')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
            {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
            <BankUpdateItem update={update} className="border-b-0 py-0" />
            {outcomes.length > 1 ? (
              <SelectField
                control={form.control}
                name="outcome"
                label={t('bank.outcome')}
                options={outcomes.map((o) => ({
                  value: o,
                  label: t(`bankUpdateOutcome.${o}`, { ns: 'status' }),
                }))}
              />
            ) : null}
            <TextareaField
              control={form.control}
              name="resolution"
              label={isQuery ? t('actions.respond_query.note') : t('bank.resolution')}
              rows={3}
            />
            <button type="submit" hidden />
          </form>
          {/* Outside the form: the upload buttons and dialog must never submit the reply. */}
          {isQuery ? (
            <LinkedRequirements
              dealId={deal.id}
              documents={requested}
              title={t('bank.requestedDocuments')}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
